import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Bomb, Check, Copy, Gem, MoveUpRight, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import apiService from '../../services/api'
import { hapticNotify } from '../../utils/haptic'
import MinesBoard from './MinesBoard'

export default function MinesHistory() {
  const { uid } = useParams()
  const { t, i18n } = useTranslation()
  const [games, setGames] = useState([])
  const [game, setGame] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const money = (amount) => Number(amount || 0).toLocaleString(i18n.language)
  const handleCopyId = async () => {
    if (!game) return
    try {
      await navigator.clipboard.writeText(game.uid || uid)
      hapticNotify('success')
      toast.success(t('mines.gameIdCopied'))
    } catch {
      hapticNotify('error')
      toast.error(t('mines.copyGameIdFailed'))
    }
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    const endpoint = uid && /^[a-f0-9]{32}$/i.test(uid) ? `/api/mines/history/${uid}/` : uid ? null : '/api/mines/history/me/'
    if (!endpoint) { setLoading(false); setError(true); return undefined }
    apiService.makeRequest(endpoint, { method: 'GET', skipBalanceSync: true }).then((data) => {
      if (active) { setGames(data.games || []); setGame(data.game || null) }
    }).catch(() => { if (active) setError(true) }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [uid])

  return (
    <div className="mx-auto max-w-xl space-y-4 px-4 py-6 text-white">
      <div className="flex items-center gap-3"><Link to={uid ? '/mines/history' : '/mines'} aria-label={t('common.back')} className="rounded-xl border border-white/10 bg-surface p-2"><ArrowLeft size={20} className="rtl:rotate-180" /></Link><h1 className="text-2xl font-black text-accent-primary">{t('mines.history')}</h1>{uid && game && <button type="button" onClick={handleCopyId} aria-label={t('mines.copyGameId')} title={t('mines.copyGameId')} className="ms-auto rounded-xl border border-white/10 bg-surface p-2 text-white transition-colors hover:border-accent-primary/40 hover:text-accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary"><Copy size={20} aria-hidden="true" /></button>}</div>
      {loading ? <p className="text-text-secondary">{t('mines.loading')}</p> : error ? <p role="alert" className="text-red-400">{t('mines.historyError')}</p> : uid ? game ? <><MinesBoard result={game} /><GameDetails game={game} money={money} t={t} /></> : <p>{t('mines.historyError')}</p> : games.length ? games.map((entry) => <HistoryEntry key={entry.uid} entry={entry} money={money} t={t} />) : <p className="text-text-secondary">{t('mines.emptyHistory')}</p>}
    </div>
  )
}

function HistoryEntry({ entry, money, t }) {
  const won = entry.result === 'won'
  const lost = entry.result === 'lost'
  const color = won ? 'text-accent-primary' : lost ? 'text-red-400' : 'text-sky-400'
  const Icon = won ? Check : lost ? Bomb : RotateCcw
  const amount = won ? entry.skin?.cost ?? entry.win : lost ? entry.amount : 0
  const mines = entry.mines_cells || []
  const opened = entry.opened || []

  return (
    <Link to={`/mines/history/${entry.uid}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-surface p-3 transition-colors hover:border-accent-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary">
      <div dir="ltr" aria-hidden="true" className="grid h-28 w-28 shrink-0 grid-cols-5 gap-0.5 rounded-xl bg-dark-primary p-2">
        {Array.from({ length: 25 }, (_, cell) => <span key={cell} className={`rounded-[3px] ${entry.boom_cell === cell ? 'bg-red-400' : mines.includes(cell) ? 'bg-red-500/35' : opened.includes(cell) ? 'bg-accent-primary/75' : 'bg-white/10'}`} />)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className={`flex items-center gap-1.5 text-sm font-bold ${color}`}><Icon size={18} className="shrink-0" aria-hidden="true" />{t(`mines.results.${entry.result}`)}</span>
        <span className={`mt-2 inline-flex items-center gap-1 text-xl font-black tabular-nums ${color}`}><Gem size={18} className="shrink-0" aria-hidden="true" /><bdi dir="ltr">{money(amount)}</bdi></span>
        {entry.skin && <span className="flex min-w-0 items-center gap-1 text-xs text-white">{entry.skin.img && <img src={entry.skin.img} alt="" className="h-5 w-5 shrink-0 object-contain" />}<span className="truncate">{entry.skin.name}</span></span>}
        <span className="mt-2 truncate text-xs text-text-secondary">{t('mines.title')} {entry.mines} · {t('mines.steps')} {entry.step}</span>
        <span className="text-[11px] text-text-secondary">{t('mines.ago', { minutes: Math.max(1, Math.floor(Number(entry.sec_ago || 0) / 60)) })}</span>
      </div>
    </Link>
  )
}

function GameDetails({ game, money, t }) {
  const lost = game.result === 'lost'
  const won = game.result === 'won'
  const amount = won ? game.skin?.cost ?? game.win : game.amount
  const color = lost ? 'text-red-400' : won ? 'text-accent-primary' : 'text-sky-400'

  return (
    <div className="mt-3 border-b border-white/20 px-1 pb-5 pt-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('mines.field')}</span>
        <span className="text-xs text-text-secondary">{t('mines.title')} <bdi dir="ltr">{game.mines}</bdi> · {t('mines.steps')} <bdi dir="ltr">{game.step}</bdi></span>
      </div>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 max-w-full">
          <span className="block text-xs text-text-secondary">{t('mines.bet')}</span>
          <span className="mt-1 inline-flex max-w-full items-start gap-1 text-xl font-bold text-white sm:text-2xl"><Gem size={17} className="mt-1 shrink-0" aria-hidden="true" /><bdi dir="ltr" className="min-w-0 break-all tabular-nums">{money(game.amount)}</bdi></span>
        </div>
        {lost ? <Bomb size={20} className="mb-1 shrink-0 text-red-400" aria-hidden="true" /> : won ? <MoveUpRight size={20} className="mb-1 shrink-0 text-text-secondary" aria-hidden="true" /> : <RotateCcw size={20} className="mb-1 shrink-0 text-sky-400" aria-hidden="true" />}
        <div className="min-w-0 max-w-full text-end">
          <span className="block text-xs text-text-secondary">{t(`mines.results.${game.result}`)} · <bdi dir="ltr">×{(Number(game.multiplier || 0) / 100).toFixed(2)}</bdi></span>
          <span className={`mt-1 inline-flex max-w-full items-start justify-end gap-1 text-xl font-black sm:text-2xl ${color}`}><Gem size={17} className="mt-1 shrink-0" aria-hidden="true" /><bdi dir="ltr" className="min-w-0 break-all tabular-nums">{money(amount)}</bdi></span>
        </div>
      </div>
      {game.items?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{game.items.map((item, index) => <span key={`${item.name}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-lg bg-dark-primary px-2 py-1 text-xs text-text-secondary">{item.img && <img src={item.img} alt="" className="h-7 w-7 shrink-0 object-contain" />}{item.name}</span>)}</div>}
      {game.skin && <div className="mt-4 flex items-center gap-2 text-sm text-accent-primary">{game.skin.img && <img src={game.skin.img} alt="" className="h-12 w-12 shrink-0 object-contain" />}<span className="min-w-0 break-words">{game.skin.name}</span></div>}
    </div>
  )
}
